import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Star, Quote } from "lucide-react";
import api from "../lib/api";
import ProductCard from "../components/ProductCard";

const HERO_VIDEO = "https://customer-assets.emergentagent.com/job_prestige-omni-store/artifacts/gx5f4yvs_vecteezy_a-woman-with-a-beautiful-face-and-long-hair_70694890.mp4";

const EDITORIALS = [
  "https://images.unsplash.com/photo-1723734820800-89bc7309acf3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHw0fHxsdXh1cnklMjBmYXNoaW9uJTIwZWRpdG9yaWFsJTIwZGFya3xlbnwwfHx8fDE3ODA0ODQxMTZ8MA&ixlib=rb-4.1.0&q=85",
  "https://images.pexels.com/photos/32194602/pexels-photo-32194602.jpeg",
  "https://images.unsplash.com/photo-1774682060997-f8959850a7d4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w8NjAzMzl8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBwZXJmdW1lJTIwYm90dGxlJTIwZGFyayUyMGJhY2tncm91bmR8ZW58MHx8fHwxNzgwNDg0MTE2fDA&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1773370812326-0ccc681834bf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwxfHxwcmVtaXVtJTIwZmxvd2VyJTIwYm91cXVldCUyMGVsZWdhbnR8ZW58MHx8fHwxNzgwNDg0MTE2fDA&ixlib=rb-4.1.0&q=85",
];

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [categories, setCategories] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [config, setConfig] = useState({});

  useEffect(() => {
    api.get("/products?featured=true&limit=8").then(r => setFeatured(r.data)).catch(() => {});
    api.get("/categories").then(r => setCategories(r.data)).catch(() => {});
    api.get("/reviews").then(r => setReviews(r.data)).catch(() => {});
    api.get("/site-config").then(r => setConfig(r.data)).catch(() => {});
  }, []);

  const hero = config.home_hero || {};
  const manifesto = config.home_manifesto || {};

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative h-screen w-full overflow-hidden">
        <video
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          poster={hero.poster_url || "https://images.unsplash.com/photo-1745560712498-4b0a0930633e?w=1920&q=85"}
          className="absolute inset-0 w-full h-full object-cover"
          data-testid="hero-video"
          key={hero.video_url || HERO_VIDEO}
        >
          <source src={hero.video_url || HERO_VIDEO} type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-b from-carbon/40 via-carbon/30 to-carbon" />
        <div className="grain absolute inset-0" />

        <div className="relative h-full max-w-7xl mx-auto px-6 md:px-12 flex flex-col justify-center">
          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.3 }}
            className="overline mb-6"
          >
            {hero.overline || "M&H Lumora · Édition Hiver MMXXVI"}
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.4, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="font-heading text-5xl sm:text-6xl md:text-8xl text-ivory font-light leading-[0.95] tracking-tighter max-w-4xl text-balance"
          >
            {hero.title_part1 || "Elegancia que"} <span className="italic text-gold">{hero.title_emphasis || "trasciende"}</span><br />{hero.title_part2 || "el tiempo"}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1 }}
            className="mt-8 text-ivory/70 max-w-lg text-base font-light leading-relaxed"
          >
            {hero.subtitle || "Joyería, floristería y cosmética concebidas como objetos de deseo. Una experiencia editorial donde cada pieza es una obra de autor."}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.2 }}
            className="flex flex-wrap gap-4 mt-10"
          >
            <Link to={hero.cta_primary_link || "/shop"} className="btn-gold" data-testid="hero-cta-shop">{hero.cta_primary_label || "Comprar colección"}</Link>
            <Link to={hero.cta_secondary_link || "/shop?is_new=true"} className="btn-outline-gold" data-testid="hero-cta-new">{hero.cta_secondary_label || "Descubrir novedades"}</Link>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.8 }}
          className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3"
        >
          <span className="text-[0.65rem] tracking-[0.3em] text-ivory/60 uppercase">Scroll</span>
          <div className="w-[1px] h-12 bg-gradient-to-b from-gold to-transparent" />
        </motion.div>
      </section>

      {/* CATEGORIES */}
      <section className="py-24 md:py-40 max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1 }}
          className="mb-16 grid md:grid-cols-2 gap-8 items-end"
        >
          <div>
            <p className="overline mb-4">Las tres casas</p>
            <h2 className="font-heading text-5xl md:text-6xl text-ivory font-light tracking-tighter leading-none">
              Tres mundos,<br /><span className="italic text-gold">una misma exigencia</span>
            </h2>
          </div>
          <p className="text-ivory/70 leading-relaxed max-w-md md:justify-self-end font-light">
            Cada categoría es atendida por nuestros maestros artesanos. Joyas eternas, composiciones florales únicas y rituales de belleza concebidos como un arte.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {categories.slice(0, 3).map((cat, i) => (
            <motion.div
              key={cat.slug}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 1, delay: i * 0.15 }}
            >
              <Link to={`/shop/${cat.slug}`} className="block group cat-card" data-testid={`category-${cat.slug}`}>
                <div className="relative aspect-[3/4] overflow-hidden bg-graphite">
                  <img src={cat.image} alt={cat.name} className="product-img w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-carbon via-carbon/30 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-8">
                    <p className="overline mb-2">{String(i + 1).padStart(2, "0")} · Maison</p>
                    <h3 className="font-heading text-4xl text-ivory mb-3 group-hover:text-gold transition-colors">{cat.name}</h3>
                    <p className="text-ivory/60 text-sm mb-5 font-light">{cat.description}</p>
                    <div className="flex items-center gap-2 text-gold text-xs uppercase tracking-[0.2em]">
                      <span>Explorar</span>
                      <ArrowRight size={14} className="group-hover:translate-x-2 transition-transform" />
                    </div>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
        {categories.length > 3 && (
          <div className="text-center mt-12">
            <Link to="/shop" className="btn-outline-gold inline-block">Ver las {categories.length} categorías</Link>
          </div>
        )}
      </section>

      {/* EDITORIAL SPLIT */}
      <section className="py-24 md:py-32 bg-graphite">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-2 gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1 }}
            className="relative aspect-[4/5]"
          >
            <img src={EDITORIALS[0]} alt="Editorial" className="w-full h-full object-cover" />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1 }}
          >
            <p className="overline mb-6">El Manifiesto</p>
            <h2 className="font-heading text-4xl md:text-6xl text-ivory font-light tracking-tighter leading-tight mb-8">
              La belleza<br />como <span className="italic text-gold">disciplina</span>
            </h2>
            <p className="text-ivory/70 leading-relaxed mb-6 font-light text-lg">
              Desde nuestro atelier en París, perseguimos una idea simple: que cada objeto sea digno de ser deseado, conservado, transmitido.
            </p>
            <p className="text-ivory/60 leading-relaxed mb-10 font-light">
              Trabajamos con los mejores artesanos europeos. Cada gema es seleccionada por su pureza, cada flor por su perfume, cada esencia por su carácter.
            </p>
            <Link to="/blog" className="btn-outline-gold inline-block">Leer el editorial</Link>
          </motion.div>
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section className="py-24 md:py-32 max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-end justify-between mb-12">
          <div>
            <p className="overline mb-4">Selección Maison</p>
            <h2 className="font-heading text-4xl md:text-5xl text-ivory font-light tracking-tighter">Piezas destacadas</h2>
          </div>
          <Link to="/shop" className="hidden md:inline-flex items-center gap-2 text-gold uppercase text-xs tracking-[0.2em] hover:gap-4 transition-all">
            Ver todo <ArrowRight size={14} />
          </Link>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {featured.slice(0, 8).map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-24 md:py-32 bg-graphite">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="overline mb-4">Voces de clientes</p>
            <h2 className="font-heading text-4xl md:text-5xl text-ivory font-light tracking-tighter">Opiniones verificadas</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {reviews.map((r, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: i * 0.1 }}
                className="bg-carbon p-8 border border-smoke"
                data-testid={`review-${i}`}
              >
                <Quote size={24} className="text-gold mb-4" />
                <div className="flex gap-1 mb-4">
                  {Array.from({ length: r.rating }).map((_, j) => <Star key={j} size={14} fill="#D4AF37" stroke="#D4AF37" />)}
                </div>
                <p className="font-heading text-lg text-ivory leading-snug mb-4">"{r.title}"</p>
                <p className="text-ivory/60 text-sm font-light leading-relaxed mb-6">{r.comment}</p>
                <div className="pt-4 border-t border-smoke">
                  <p className="text-sm text-ivory">{r.name}</p>
                  <p className="text-xs text-ivory/40 mt-1">{r.product}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* INSTAGRAM FEED */}
      <section className="py-24 md:py-32 max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-12">
          <p className="overline mb-4">@luxemaison</p>
          <h2 className="font-heading text-4xl md:text-5xl text-ivory font-light tracking-tighter">Síganos en Instagram</h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {EDITORIALS.map((img, i) => (
            <motion.a
              key={i}
              href="https://instagram.com" target="_blank" rel="noreferrer"
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: i * 0.1 }}
              className="aspect-square overflow-hidden block bg-graphite"
              data-testid={`insta-${i}`}
            >
              <img src={img} alt="" className="product-img w-full h-full object-cover" />
            </motion.a>
          ))}
        </div>
      </section>
    </div>
  );
}
